/**
 * Mobile-internal drop designation modal for Free Agents page.
 * Per Tony's mobile architecture rule: mobile-internal port of desktop DropDesignationModal.
 *
 * Context-driven render:
 *   - 'fa_add' context: standard release flow when roster is full
 *   - 'waiver_claim' context: waiver-priority-aware flow with expiry display
 *
 * Divergence from desktop (9o.6.a per audit F.2 prune-and-port):
 *   - PlayerLink replaced with mobile player-modal opener pattern (onPlayerClick prop)
 *   - Vertical/stacked layout replacing desktop Tailwind grid
 *   - Inline styles + dynastyTheme tokens (no Tailwind class strings)
 *   - isOptionContract imported from shared services/contractUtils
 */

import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import { isOptionContract } from '../../../services/contractUtils';
import MobilePlayerLink from './MobilePlayerLink';

const C = dynastyTheme.tokens.colors;

const MobileDropDesignationModal = ({
  isOpen,
  onClose,
  onConfirm,
  onRequestPlan = null,
  playerBeingAdded,
  currentRoster,
  leagueId,
  context,
  isRosterFull,
  hasActiveSlot = null,
  hasBenchSlot = null,
  waiverPriority,
  totalTeams,
  waiverExpiresAt,
  onPlayerClick = null,
}) => {
  const [selectedDropId, setSelectedDropId] = useState(null);
  const [deadMoneyCache, setDeadMoneyCache] = useState({});
  const [deadMoneyLoading, setDeadMoneyLoading] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const handleRowSelect = async (player) => {
    const id = player.ids?.league_player;
    setSelectedDropId(id);

    const contractType = player.league_context?.financial?.contract_type;
    // Fetch dead-money impact only for OP-with-years contracts (length>2; matches desktop).
    if (contractType?.startsWith('OP') && contractType.length > 2 && /^\d+$/.test(contractType.slice(2))) {
      if (!deadMoneyCache[id]) {
        setDeadMoneyLoading(id);
        try {
          const result = await leaguesAPI.getDropImpact(leagueId, id);
          setDeadMoneyCache((prev) => ({ ...prev, [id]: result }));
        } catch (_err) {
          setDeadMoneyCache((prev) => ({ ...prev, [id]: { error: true } }));
        } finally {
          setDeadMoneyLoading(null);
        }
      }
    }
  };

  const handleConfirmClick = () => {
    if (!confirming) {
      setConfirming(true);
    } else {
      onConfirm(selectedDropId);
      setConfirming(false);
    }
  };

  const handleClose = () => {
    setConfirming(false);
    onClose();
  };

  const isConfirmDisabled =
    deadMoneyLoading !== null || (selectedDropId === null && isRosterFull);

  // DEFECT 2 Phase 1 — trade_placement context: parity with the desktop
  // DropDesignationModal. Same UX as waiver_claim with trade-flavored copy.
  const header =
    context === 'waiver_claim' ? 'Submit Waiver Claim'
    : context === 'trade_placement' ? 'Place Incoming Player'
    : 'Your roster is full';
  const dropListLabel =
    context === 'waiver_claim' || context === 'trade_placement'
      ? 'Designate a player to release:'
      : 'Select a player to release:';
  const confirmLabel = confirming
    ? 'Are you sure?'
    : context === 'waiver_claim'
    ? 'Submit Waiver Claim'
    : context === 'trade_placement'
    ? 'Confirm Placement'
    : 'Confirm — Add & Release';
  const showNoDropOption = (context === 'waiver_claim' || context === 'trade_placement') && isRosterFull === false;
  // Bench-only landing copy (no active slot but bench open).
  // Active-slot copy stays the generic "open roster spot" wording.
  const noDropLabel =
    hasActiveSlot === false && hasBenchSlot === true
      ? 'I have an open bench spot (no drop needed — lands on bench)'
      : 'I have an open roster spot (no drop needed)';
  const rosterFullHint =
    (context === 'waiver_claim' || context === 'trade_placement') && isRosterFull === true
      ? 'Active and bench are full — this needs a roster move.'
      : null;

  if (!isOpen) return null;

  return (
    <div data-testid="mobile-drop-modal" style={styles.overlay}>
      <div style={styles.backdrop} onClick={handleClose} />
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>{header}</h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={styles.closeButton}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Player being added */}
          {playerBeingAdded && (
            <div style={styles.addingCard}>
              <p style={styles.addingLabel}>Adding</p>
              <div style={styles.addingRow}>
                <div>
                  <MobilePlayerLink
                    mlbId={playerBeingAdded.ids?.mlb}
                    name={playerBeingAdded.info?.full_name || ''}
                    onClick={onPlayerClick}
                    style={{ fontWeight: 600, fontSize: 14 }}
                  />
                  <span style={styles.playerMeta}>
                    {playerBeingAdded.info?.position || ''}
                    {playerBeingAdded.info?.position && playerBeingAdded.info?.mlb_team ? ' · ' : ''}
                    {playerBeingAdded.info?.mlb_team || ''}
                  </span>
                </div>
                <span style={styles.addingPrice}>
                  {playerBeingAdded.league_context?.financial?.baseline_price != null
                    ? `$${Math.round(playerBeingAdded.league_context.financial.baseline_price)}`
                    : ''}
                </span>
              </div>
            </div>
          )}

          {/* Drop list label. When an open slot exists (requiresDrop=false),
              the release is OPTIONAL — do not command a designation. */}
          <p style={styles.dropListLabel}>{(showNoDropOption && context === 'trade_placement') ? 'Optional: release a player to make room' : dropListLabel}</p>

          {/* Roster-full hint (waiver_claim, no active or bench slot) */}
          {rosterFullHint && (
            <p data-testid="roster-full-hint" style={styles.rosterFullHint}>
              {rosterFullHint}
            </p>
          )}

          {/* Roster rows (stacked, large touch targets).
              Filter out former players — the canonical /my-roster + /teams/{id}/roster
              endpoints bundle former players with current roster. The drop-designation
              contract is "select a CURRENT-roster player". Including former players
              caused waiver claims to fail backend validation (drop_player.team_id !=
              team_id at waiver_service.py:388 + roster_service.py:240).
              CANONICAL MARKER: player.info?.is_historical (NESTED under .info per
              accrued_stats_repository.py:994). NOT top-level p.is_historical. */}
          <div style={styles.rosterList}>
            {/* No-drop option FIRST when an open slot exists — honors capacity's
                requiresDrop=false (selected by default; Confirm needs no release).
                Previously rendered LAST, below the roster list, so it was buried. */}
            {showNoDropOption && (
              <label
                data-testid="no-drop-option"
                style={{
                  ...styles.rosterRow,
                  ...(selectedDropId === null ? styles.rosterRowSelected : {}),
                }}
              >
                <input
                  type="radio"
                  name="drop-selection"
                  value="__none__"
                  checked={selectedDropId === null}
                  onChange={() => setSelectedDropId(null)}
                  style={styles.radio}
                />
                <span style={styles.noDropText}>{noDropLabel}</span>
              </label>
            )}

            {(currentRoster || []).filter(p => !p.info?.is_historical).map((player) => {
              const id = player.ids?.league_player;
              const isSelected = selectedDropId === id;
              const contractType = player.league_context?.financial?.contract_type || '';
              const showBadge = isOptionContract(contractType);
              const cachedImpact = deadMoneyCache[id];
              const isLoadingThis = deadMoneyLoading === id;

              return (
                <div key={id}>
                  <label
                    data-testid={`drop-row-${id}`}
                    style={{
                      ...styles.rosterRow,
                      ...(isSelected ? styles.rosterRowSelected : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name="drop-selection"
                      value={id}
                      checked={isSelected}
                      onChange={() => handleRowSelect(player)}
                      style={styles.radio}
                    />
                    <div style={styles.rosterRowContent}>
                      <div style={styles.rosterRowHeader}>
                        <MobilePlayerLink
                          mlbId={player.ids?.mlb}
                          name={player.info?.full_name || ''}
                          onClick={onPlayerClick}
                          style={{ fontWeight: 600, fontSize: 14 }}
                        />
                        <span style={styles.rosterRowPosition}>{player.info?.position || ''}</span>
                        {showBadge && (
                          <span style={styles.opBadge}>{contractType}</span>
                        )}
                      </div>
                    </div>
                    <div style={styles.rosterRowRight}>
                      {player.league_context?.financial?.contract_salary != null && (
                        <span style={styles.salary}>
                          ${player.league_context.financial.contract_salary}
                        </span>
                      )}
                      {player.league_context?.financial?.contract_years != null && (
                        <span style={styles.years}>
                          {' '}{player.league_context.financial.contract_years}yr
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Dead-money loading */}
                  {isSelected && isLoadingThis && (
                    <div style={styles.deadMoneyLoading}>
                      <span style={styles.deadMoneyText}>Checking contract impact…</span>
                    </div>
                  )}
                  {/* Dead-money warning */}
                  {isSelected && !isLoadingThis && cachedImpact && cachedImpact.has_dead_money && cachedImpact.warning_message && (
                    <div style={styles.deadMoneyWarning}>
                      <AlertTriangle size={14} style={{ color: C.gold.soft, flexShrink: 0, marginTop: 2 }} />
                      <span style={styles.deadMoneyWarningText}>{cachedImpact.warning_message}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Waiver info (waiver_claim only) */}
          {context === 'waiver_claim' && (
            <div style={styles.waiverInfo}>
              {waiverPriority != null && totalTeams != null && (
                <p style={styles.waiverInfoText}>
                  Your waiver priority:{' '}
                  <span style={styles.waiverInfoValue}>
                    #{waiverPriority} of {totalTeams}
                  </span>
                </p>
              )}
              {waiverExpiresAt && (
                <p style={styles.waiverInfoText}>
                  Claims process:{' '}
                  <span style={styles.waiverInfoValue}>
                    {new Date(waiverExpiresAt).toLocaleString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })}
                  </span>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button type="button" onClick={handleClose} style={styles.cancelButton}>
            Cancel
          </button>
          {context === 'waiver_claim' && hasActiveSlot === false && onRequestPlan && (
            <button
              type="button"
              onClick={onRequestPlan}
              data-testid="use-plan-button"
              style={styles.planButton}
            >
              Rearrange roster to put in active {playerBeingAdded?.info?.position || ''}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirmClick}
            disabled={isConfirmDisabled}
            style={{
              ...styles.confirmButton,
              ...(confirming ? styles.confirmButtonDanger : {}),
              ...(isConfirmDisabled ? styles.confirmButtonDisabled : {}),
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};


const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  },
  backdrop: {
    position: 'absolute',
    inset: 0,
    backgroundColor: withAlpha(C.black, 0.7),
  },
  panel: {
    position: 'relative',
    width: '100%',
    maxWidth: 480,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: dynastyTheme.tokens.colors.neutral[900],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    borderRadius: 12,
    boxShadow: `0 25px 50px -12px ${withAlpha(C.black, 0.5)}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: C.white,
    margin: 0,
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: dynastyTheme.tokens.colors.neutral[300],
    cursor: 'pointer',
    padding: 6,
    minWidth: 36,
    minHeight: 36,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  addingCard: {
    backgroundColor: dynastyTheme.tokens.colors.neutral[800],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    borderRadius: 8,
    padding: 10,
  },
  addingLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: dynastyTheme.tokens.colors.neutral[400],
    marginBottom: 4,
  },
  addingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  playerMeta: {
    marginLeft: 8,
    fontSize: 12,
    color: dynastyTheme.tokens.colors.neutral[300],
  },
  addingPrice: {
    fontSize: 13,
    fontWeight: 500,
    color: dynastyTheme.tokens.colors.primary,
  },
  dropListLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: dynastyTheme.tokens.colors.neutral[300],
    margin: 0,
  },
  rosterFullHint: {
    fontSize: 12,
    color: C.gold.soft,
    margin: 0,
    padding: '8px 10px',
    borderRadius: 6,
    backgroundColor: withAlpha(C.warning, 0.12),
    border: `1px solid ${withAlpha(C.warning, 0.3)}`,
  },
  rosterList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  rosterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    minHeight: 56,
    borderRadius: 8,
    cursor: 'pointer',
    backgroundColor: dynastyTheme.tokens.colors.neutral[800],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    transition: 'background-color 0.15s, border-color 0.15s',
  },
  rosterRowSelected: {
    backgroundColor: `${dynastyTheme.tokens.colors.primary}22`,
    border: `1px solid ${dynastyTheme.tokens.colors.primary}`,
  },
  rosterRowContent: {
    flex: 1,
    minWidth: 0,
  },
  rosterRowHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  rosterRowPosition: {
    fontSize: 11,
    color: dynastyTheme.tokens.colors.neutral[300],
  },
  opBadge: {
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    color: C.gold.soft,
    backgroundColor: withAlpha(C.warning, 0.2),
    border: `1px solid ${withAlpha(C.warning, 0.3)}`,
  },
  rosterRowRight: {
    flexShrink: 0,
    textAlign: 'right',
  },
  salary: {
    fontSize: 13,
    fontWeight: 500,
    color: C.white,
  },
  years: {
    fontSize: 11,
    color: dynastyTheme.tokens.colors.neutral[300],
  },
  radio: {
    flexShrink: 0,
    width: 18,
    height: 18,
    cursor: 'pointer',
  },
  deadMoneyLoading: {
    margin: '4px 12px 0',
    padding: '8px 10px',
    borderRadius: 6,
    backgroundColor: withAlpha(C.goldBase, 0.1),
    border: `1px solid ${withAlpha(C.goldBase, 0.2)}`,
  },
  deadMoneyText: {
    fontSize: 11,
    color: dynastyTheme.tokens.colors.neutral[300],
  },
  deadMoneyWarning: {
    margin: '4px 12px 0',
    padding: '8px 10px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: withAlpha(C.goldBase, 0.1),
    border: `1px solid ${withAlpha(C.goldBase, 0.3)}`,
  },
  deadMoneyWarningText: {
    fontSize: 11,
    color: C.amber[300],
  },
  noDropText: {
    fontSize: 13,
    color: dynastyTheme.tokens.colors.neutral[300],
  },
  waiverInfo: {
    backgroundColor: dynastyTheme.tokens.colors.neutral[900],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    borderRadius: 8,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  waiverInfoText: {
    fontSize: 11,
    color: dynastyTheme.tokens.colors.neutral[300],
    margin: 0,
  },
  waiverInfoValue: {
    color: C.white,
  },
  footer: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    padding: '12px 16px',
    borderTop: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
  },
  cancelButton: {
    padding: '10px 16px',
    minHeight: 44,
    borderRadius: 6,
    backgroundColor: dynastyTheme.tokens.colors.neutral[800],
    color: dynastyTheme.tokens.colors.neutral[200],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmButton: {
    padding: '10px 16px',
    minHeight: 44,
    borderRadius: 6,
    backgroundColor: dynastyTheme.tokens.colors.primary,
    color: C.black,
    border: 'none',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  planButton: {
    padding: '10px 14px',
    minHeight: 44,
    borderRadius: 6,
    backgroundColor: 'transparent',
    color: dynastyTheme.tokens.colors.primary,
    border: `1px solid ${dynastyTheme.tokens.colors.primary}`,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmButtonDanger: {
    backgroundColor: dynastyTheme.tokens.colors.error,
    color: C.white,
  },
  confirmButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

export default MobileDropDesignationModal;
